import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  IconPicker,
  AddCardTypeModal,
  EditCardTypesModal,
  AddBasicFieldModal,
} from '../AddCardTypeModal';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, useSortable, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import adminConsoleService, {
  type ConfigValueItem,
  type ConfigValueDetail,
  type ConfigValueLayer,
  type CustomField,
} from '@services/adminConsoleService';
import { Modal } from '@/components/utils/Modal';
import CreatableSelect from 'react-select/creatable';

// ─── Sortable field card ──────────────────────────────────────────────────────

const SortableFieldCard: React.FC<{
  field: CustomField;
  editLayer: React.Dispatch<React.SetStateAction<ConfigValueDetail | null>>;
}> = ({ field, editLayer }) =>
{
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.CustomFieldId,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const removeDetail = async () =>
  {
    setSaving(true);
    try
    {
      await adminConsoleService.deleteLayerField(field);
      editLayer(prev =>
      {
        if (!prev) return prev;
        const layerEntry = prev.Layers.find(x => x.LayerId === field.LayerId);
        if (layerEntry)
        {
          const index = layerEntry.CustomFields.findIndex(
            x => x.CustomFieldId === field.CustomFieldId,
          );
          if (index !== -1) layerEntry.CustomFields.splice(index, 1);
        }
        return prev;
      });
    }
    finally
    {
      setSaving(false);
      setOpenRemoveModal(false);
    }
  };

  return (
    <>
      <Modal
        open={openRemoveModal}
        onClose={() => setOpenRemoveModal(false)}
        preventCloseClickOut
        title="Remove Field From Layer"
      >
        <div>
          <p className="pt-[10px] pb-[8px] px-[20px] text-sm">{`Are you sure you want to remove field "${field.Name}" from layer "Default"?`}</p>
          <div className="pt-3 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
            <button
              type="button"
              onClick={removeDetail}
              disabled={saving}
              className="bg-corgee-blue hover:bg-corgee-BlueHover border text-white text-xs h-7 px-3 py-1 disabled:opacity-60"
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => setOpenRemoveModal(false)}
              className="bg-brightGray hover:bg-corgee-brightGrayHover border text-white text-xs h-7 px-3 py-1"
            >
              No
            </button>
          </div>
        </div>
      </Modal>
      <div
        ref={setNodeRef}
        style={style}
        className="relative border border-[#e0e0e0] rounded bg-white w-[105px] flex-shrink-0 select-none"
      >
        <div className="flex items-center justify-between px-1.5 pt-1 pb-0.5">
          <span
            {...attributes}
            {...listeners}
            className="grid grid-cols-2 gap-[2px] cursor-grab active:cursor-grabbing opacity-40 hover:opacity-80 touch-none"
            title="Drag to reorder"
          >
            {[0, 1, 2, 3].map(i => (
              <span key={i} className="w-[3px] h-[3px] rounded-full bg-[#888] block" />
            ))}
          </span>
          <button
            className="text-[#bbb] hover:text-[#e57373] transition-colors leading-none"
            onClick={() => setOpenRemoveModal(true)}
          >
            <span className="material-icons text-[13px]">close</span>
          </button>
        </div>
        <div
          className="px-2 pb-0.5 text-[11px] text-[#62aacb] font-medium leading-tight truncate"
          title={field.Name}
        >
          {field.Name}
        </div>
        <div className="px-2 pb-2 text-[11px] text-[#333] font-semibold leading-tight">Not Set</div>
      </div>
    </>
  );
};

// ─── Layer section (with sortable context) ────────────────────────────────────

interface LayerSectionProps {
  layer: ConfigValueLayer;
  selectedCT: ConfigValueItem;
  onLayerChange: (updated: ConfigValueLayer) => void;
  onAddDetails: () => void;
  dragHandle?: React.ReactNode;
  editLayer: React.Dispatch<React.SetStateAction<ConfigValueDetail | null>>;
}

const LayerSection: React.FC<LayerSectionProps> = ({
  layer,
  selectedCT,
  onLayerChange,
  onAddDetails: _onAddDetails,
  dragHandle,
  editLayer,
}) =>
{
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Edit Card Types modal
  const [showAddCardTypeModal, setShowAddCardTypeModal] = useState(false);

  const activeFields = layer.CustomFields.filter(f => !f.IsDeleted).sort(
    (a, b) => a.OrderInLayer - b.OrderInLayer,
  );

  const badgeBg = layer.Color && layer.Color !== 'transparent' ? layer.Color : '#e8e8e8';
  const badgeText = layer.Color && layer.Color !== 'transparent' ? '#fff' : '#555';

  const handleDragEnd = useCallback(
    (event: DragEndEvent) =>
    {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = activeFields.findIndex(f => f.CustomFieldId === active.id);
      const newIndex = activeFields.findIndex(f => f.CustomFieldId === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(activeFields, oldIndex, newIndex).map((f, idx) => ({
        ...f,
        OrderInLayer: idx + 1,
      }));

      const deletedFields = layer.CustomFields.filter(f => f.IsDeleted);
      const updatedLayer: ConfigValueLayer = {
        ...layer,
        CustomFields: [...reordered, ...deletedFields],
      };

      onLayerChange(updatedLayer);

      adminConsoleService.sortFieldsInLayer(updatedLayer).catch(() =>
      {
        onLayerChange(layer);
      });
    },
    [activeFields, layer, onLayerChange],
  );

  const openAddCardType = () =>
  {
    openAddCardType;
    setShowAddCardTypeModal(true);
  };

  return (
    <>
      {/* ── Edit Card Types modal ── */}
      {showAddCardTypeModal && (
        <AddBasicFieldModal
          open={showAddCardTypeModal}
          layerId={layer.LayerId}
          configValueId={selectedCT.ConfigValueID}
          onClose={() => setShowAddCardTypeModal(false)}
          onSaved={editLayer}
        />
      )}

      <div className="mb-5 border border-[#e0e0e0] rounded bg-[#fafafa] dark:bg-[#363838]">
        <div className="flex items-center justify-between px-3 py-2 border-b border-[#e8e8e8]">
          <div className="flex items-center gap-2">
            {dragHandle}
            <span
              className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
              style={{ backgroundColor: badgeBg, color: badgeText }}
            >
              {layer.Name}
            </span>
          </div>
          <button
            className="text-[#62aacb] text-[12px] flex items-center gap-1"
            onClick={openAddCardType}
          >
            <span className="material-icons text-[14px]">add</span>
            <span className="hover:underline">Add detail</span>
          </button>
        </div>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={activeFields.map(f => f.CustomFieldId)}
            strategy={rectSortingStrategy}
          >
            <div className="p-3 flex flex-wrap gap-2 min-h-[60px] dark:bg-[#4a4c4c]">
              {activeFields.length === 0 ? (
                <span className="text-[11px] text-[#bbb] italic py-2">No details configured.</span>
              ) : (
                activeFields.map(f => (
                  <SortableFieldCard key={f.CustomFieldId} field={f} editLayer={editLayer} />
                ))
              )}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </>
  );
};

// ─── Sortable layer wrapper ───────────────────────────────────────────────────

const SortableLayerSection: React.FC<{
  layer: ConfigValueLayer;
  selectedCT: ConfigValueItem;
  onLayerChange: (updated: ConfigValueLayer) => void;
  onAddDetails: () => void;
  editLayer: React.Dispatch<React.SetStateAction<ConfigValueDetail | null>>;
}> = ({ layer, selectedCT, onLayerChange, onAddDetails, editLayer }) =>
{
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: layer.LayerId,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  const dragHandle = (
    <span
      {...attributes}
      {...listeners}
      className="grid grid-cols-2 gap-[2px] cursor-grab active:cursor-grabbing opacity-40 hover:opacity-80 flex-shrink-0 touch-none"
      title="Drag to reorder layer"
    >
      {[0, 1, 2, 3, 4, 5].map(i => (
        <span key={i} className="w-[3px] h-[3px] rounded-full bg-[#888] block" />
      ))}
    </span>
  );

  return (
    <div ref={setNodeRef} style={style}>
      <LayerSection
        layer={layer}
        selectedCT={selectedCT}
        onLayerChange={onLayerChange}
        onAddDetails={onAddDetails}
        editLayer={editLayer}
        dragHandle={dragHandle}
      />
    </div>
  );
};

// ─── Main tab ─────────────────────────────────────────────────────────────────

export const CardTypesTab: React.FC = () =>
{
  const [cardTypes, setCardTypes] = useState<ConfigValueItem[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ConfigValueDetail | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editCardName, setEditCardName] = useState(false);
  const [listOptionEditName, setListOptionEditName] = useState<any[]>([]);

  // Icon picker
  const [showIconPicker, setShowIconPicker] = useState(false);
  const iconAnchorRef = useRef<HTMLDivElement>(null);

  // Add Card Type modal
  const [showAddModal, setShowAddModal] = useState(false);

  // Edit Card Types modal
  const [showEditModal, setShowEditModal] = useState(false);

  // Layer-level drag sensors
  const layerSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  // Load card type list once
  useEffect(() =>
  {
    adminConsoleService
      .getConfigValueAll()
      .then(data =>
      {
        const enabled = data;
        setCardTypes(enabled);
        if (enabled.length > 0 && enabled[0]) setSelectedId(enabled[0].ConfigValueID);
      })
      .catch(() => setError('Failed to load card types.'))
      .finally(() => setLoadingList(false));
  }, []);

  // Load detail whenever selection changes
  useEffect(() =>
  {
    if (selectedId === null) return;
    setLoadingDetail(true);
    setDetail(null);
    adminConsoleService
      .getConfigValueById(selectedId)
      .then(setDetail)
      .catch(() => setError('Failed to load card type details.'))
      .finally(() => setLoadingDetail(false));
  }, [selectedId]);

  // Icon change
  const handleIconSelect = useCallback(
    (icon: string) =>
    {
      if (!detail || selectedId === null) return;
      setDetail(prev => (prev ? { ...prev, Icon: icon } : prev));
      setCardTypes(prev =>
        prev.map(ct => (ct.ConfigValueID === selectedId ? { ...ct, Icon: icon } : ct)),
      );
      adminConsoleService.updateSideMenuIcon(selectedId, icon).catch(() =>
      {
        setError('Failed to update icon.');
      });
    },
    [detail, selectedId],
  );

  // Called by LayerSection when fields are reordered
  const handleLayerChange = useCallback((updatedLayer: ConfigValueLayer) =>
  {
    setDetail(prev =>
      prev
        ? {
          ...prev,
          Layers: prev.Layers.map(l => (l.LayerId === updatedLayer.LayerId ? updatedLayer : l)),
        }
        : prev,
    );
  }, []);

  // Called by LayerSection when fields are reordered
  const handleReloadLayer = useCallback(
    (_updatedLayer?: ConfigValueLayer) =>
    {
      if (selectedId === null) return;
      setLoadingDetail(true);
      setDetail(null);
      adminConsoleService
        .getConfigValueById(selectedId)
        .then(setDetail)
        .catch(() => setError('Failed to load card type details.'))
        .finally(() => setLoadingDetail(false));
    },
    [selectedId],
  );

  // Called when layers are reordered via drag
  const handleLayerDragEnd = useCallback(
    (event: DragEndEvent) =>
    {
      const { active, over } = event;
      if (!over || active.id === over.id || !detail || selectedId === null) return;

      const sortedLayers = detail.Layers.slice().sort(
        (a, b) => a.OrderInConfigValue - b.OrderInConfigValue,
      );
      const oldIndex = sortedLayers.findIndex(l => l.LayerId === active.id);
      const newIndex = sortedLayers.findIndex(l => l.LayerId === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(sortedLayers, oldIndex, newIndex).map((l, idx) => ({
        ...l,
        OrderInConfigValue: idx,
        PriorityInConfigValue: idx,
      }));

      setDetail(prev => (prev ? { ...prev, Layers: reordered } : prev));

      const payload = reordered.map((l, idx) => ({
        ConfigValueID: selectedId,
        LayerId: l.LayerId,
        PriorityInConfigValue: idx,
      }));

      adminConsoleService.sortLayers(payload).catch(() =>
      {
        setDetail(prev => (prev ? { ...prev, Layers: sortedLayers } : prev));
        setError('Failed to reorder layers.');
      });
    },
    [detail, selectedId],
  );

  const prepareSelect = async () =>
  {
    setEditCardName(true);
    if (!selectedCT) return;
    const resposne = await adminConsoleService.getbyconfigitem(selectedCT.ConfigItemID);
    setListOptionEditName((resposne as any)?.Values ?? []);
  };

  const handleCreatableColumnChange = async (e: any) =>
  {
    await adminConsoleService.updateCardTypeSelected({
      CardTypes: [
        { ConfigValueID: e?.ConfigValueId, ConfigItemID: detail?.ConfigItemID, Text: e.Name },
      ],
    });
    setEditCardName(false);
    setDetail(prev =>
    {
      if (!prev) return prev;
      prev.Value = e.Name;
      return prev;
    });
    setCardTypes(prev =>
    {
      const found = prev.find(x => x.ConfigValueID === detail?.ConfigValueID);
      if (found) found.SideMenuText = e.Name;
      return prev;
    });
  };

  // Handle new option click (create new column)
  const onNewOptionClick = async (inputValue: any) =>
  {
    await adminConsoleService.updateCardTypeSelected({
      CardTypes: [{ ConfigValueID: -1, ConfigItemID: detail?.ConfigItemID, Text: inputValue }],
    });
    setEditCardName(false);
    setDetail(prev =>
    {
      if (!prev) return prev;
      prev.Value = inputValue;
      return prev;
    });
    setCardTypes(prev =>
    {
      const found = prev.find(x => x.ConfigValueID === detail?.ConfigValueID);
      if (found) found.SideMenuText = inputValue;
      return prev;
    });
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
  {
    if (e.key === 'Escape') setEditCardName(false);
  };

  const selectedCT = cardTypes.find(ct => ct.ConfigValueID === selectedId);

  return (
    <div className="flex flex-col h-full">
      {/* ── Card type sub-tabs ── */}
      <div className="flex-shrink-0 bg-white border-b border-[#e0e0e0] overflow-x-auto">
        <div className="flex items-center min-w-max">
          {loadingList ? (
            <span className="px-4 py-2 text-[12px] text-[#aaa]">Loading…</span>
          ) : (
            cardTypes.map(ct => (
              <button
                key={ct.ConfigValueID}
                onClick={() => setSelectedId(ct.ConfigValueID)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-[12px] whitespace-nowrap border-b-2 transition-colors ${
                  ct.ConfigValueID === selectedId
                    ? 'border-[#62aacb] text-[#62aacb] font-medium'
                    : 'border-transparent text-[#555] hover:text-[#62aacb]'
                }`}
              >
                <i className={`${ct.Icon} text-[14px]`} />
                {ct.SideMenuText}
              </button>
            ))
          )}

          <button
            className="ml-1 flex-shrink-0 w-[32px] h-[32px] flex items-center justify-center text-[#62aacb] hover:text-[#4e99ba]"
            title="Add card type"
            onClick={() => setShowAddModal(true)}
          >
            <span className="material-icons text-[20px] ">add_circle_outline</span>
          </button>

          <div className="flex-1 min-w-[16px]" />

          <button
            className="flex-shrink-0 px-4 py-2.5 text-[12px] text-[#62aacb] hover:underline border-b-2 border-transparent"
            onClick={() => setShowEditModal(true)}
          >
            Edit
          </button>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="mx-6 mt-3 px-3 py-2 bg-[#fdecea] border border-[#e57373] rounded text-[12px] text-[#c62828] flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="material-icons text-[14px]">error_outline</span>
            {error}
          </div>
          <button onClick={() => setError(null)} className="hover:opacity-70">
            <span className="material-icons text-[14px]">close</span>
          </button>
        </div>
      )}

      {/* ── Add Card Type modal ── */}
      {showAddModal && (
        <AddCardTypeModal
          onClose={() => setShowAddModal(false)}
          onSuccess={updatedList =>
          {
            const enabled = updatedList.filter(ct => ct.IsEnabled);
            setCardTypes(enabled);
            if (enabled.length > 0 && !enabled.find(ct => ct.ConfigValueID === selectedId))
            {
              setSelectedId(enabled[0]?.ConfigValueID ?? null);
            }
          }}
        />
      )}

      {/* ── Edit Card Types modal ── */}
      {showEditModal && (
        <EditCardTypesModal
          onClose={() => setShowEditModal(false)}
          onSuccess={updatedList =>
          {
            const enabled = updatedList.filter(ct => ct.IsEnabled);
            setCardTypes(enabled);
            if (enabled.length > 0 && !enabled.find(ct => ct.ConfigValueID === selectedId))
            {
              setSelectedId(enabled[0]?.ConfigValueID ?? null);
            }
          }}
        />
      )}

      {/* ── Body ── */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {loadingDetail ? (
          <div className="flex items-center justify-center h-full">
            <span className="material-icons animate-spin text-[#62aacb] text-[32px]">refresh</span>
          </div>
        ) : detail ? (
          <>
            <h3 className="text-[15px] font-semibold text-[#62aacb] mb-4">Edit Card Type</h3>

            {/* Card type identity */}
            <div className="flex items-start gap-4 mb-6">
              <div
                ref={iconAnchorRef}
                onClick={() => setShowIconPicker(v => !v)}
                className="border border-[#ddd] rounded p-3 flex flex-col items-center gap-1  bg-white cursor-pointer hover:border-[#62aacb] hover:shadow-sm transition-all group relative"
                title="Click to change icon"
              >
                <i className={`${detail.Icon} text-[28px] text-[#333]`} />
                <span className="absolute -bottom-1 right-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="material-icons text-[12px] text-[#62aacb]">edit</span>
                </span>
              </div>
              {editCardName ? (
                <>
                  <CreatableSelect
                    value={detail.Value}
                    defaultValue={detail.Value}
                    options={listOptionEditName}
                    onChange={handleCreatableColumnChange}
                    onCreateOption={onNewOptionClick}
                    onKeyDown={handleRenameKeyDown}
                    autoFocus={true}
                    placeholder="Select or create column"
                    getOptionLabel={(option: any) => option?.label ?? option?.Name ?? ''}
                    getOptionValue={(option: any) => String(option?.ConfigValueId)}
                    formatCreateLabel={x => `Add ${x}`}
                    theme={theme => ({
                      ...theme,
                      borderRadius: 0,
                      colors: {
                        ...theme.colors,
                        primary25: '#e0f2fe',
                        primary: '#dedede',
                      },
                    })}
                    styles={{
                      control: base => ({
                        ...base,
                        borderRadius: 5,
                        width: 250,
                      }),
                      menu: base => ({
                        ...base,
                        marginTop: 0,
                      }),
                    }}
                  />
                </>
              ) : (
                <span
                  className="text-[11px] text-[#444] text-left truncate w-full cursor-text text-lg max-w-72 hover:underline"
                  onDoubleClick={() => prepareSelect()}
                  title="Double-click to rename"
                >
                  {detail.Value}
                </span>
              )}
            </div>

            {/* Icon picker */}
            {showIconPicker && (
              <IconPicker
                anchorRef={iconAnchorRef}
                currentIcon={detail.Icon}
                onSelect={handleIconSelect}
                onClose={() => setShowIconPicker(false)}
              />
            )}

            {/* Layers */}
            {(() =>
            {
              const sortedLayers = detail.Layers.slice().sort(
                (a, b) => a.OrderInConfigValue - b.OrderInConfigValue,
              );
              return (
                <DndContext
                  sensors={layerSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleLayerDragEnd}
                >
                  <SortableContext
                    items={sortedLayers.map(l => l.LayerId)}
                    strategy={rectSortingStrategy}
                  >
                    {sortedLayers.map(layer => (
                      <SortableLayerSection
                        key={layer.LayerId}
                        layer={layer}
                        selectedCT={selectedCT!}
                        onLayerChange={handleLayerChange}
                        onAddDetails={() => handleReloadLayer()}
                        editLayer={setDetail}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              );
            })()}
          </>
        ) : !loadingList ? (
          <div className="flex flex-col items-center justify-center h-full text-[#aaa] text-sm gap-2">
            <span className="material-icons text-[32px]">category</span>
            Select a card type to edit.
          </div>
        ) : null}
      </div>
    </div>
  );
};
